import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getAdminSessionInfos,
  getAdminSessionInfoById,
  createAdminSessionInfo,
  updateAdminSessionInfo,
  deleteAdminSessionInfo,
} from "../../api/admin/sessionInfo.api";

/* ============================================================
   DEBUG LOGGER
   Payloads contain student data, so logging is skipped in
   production builds. Behaviour in development is unchanged.
============================================================ */

const debug = (...args) => {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    return;
  }
  console.log(...args);
};

/* ============================================================
   FETCH SESSION INFO LIST
   Existing call  : fetchSessionInfos({ page, limit, search })
   New (optional) : date_from, date_to (YYYY-MM-DD), student_id
============================================================ */

export const fetchSessionInfos = createAsyncThunk(
  "admin/sessionInfo/fetch",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
      date_from = "",
      date_to = "",
      student_id = "",
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await getAdminSessionInfos({
        page,
        limit,
        search,
        date_from,
        date_to,
        student_id,
      });

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      // The slice reads payload.data and payload.meta
      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch Session Instances."
      );
    }
  }
);

/* ============================================================
   FETCH SINGLE SESSION INFO (DETAILED INSTANCE WITH ROSTER)
   Result includes lessons[] and attendance_roster
============================================================ */

export const fetchSessionInfoById = createAsyncThunk(
  "admin/sessionInfo/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await getAdminSessionInfoById(id);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch Session Instance details."
      );
    }
  }
);

/* ============================================================
   CREATE SESSION INFO (INITIALIZE INSTANCE + LESSONS + ATTENDANCE)
   payload may contain lessons[] and attendance_roster[]
============================================================ */

export const addSessionInfo = createAsyncThunk(
  "admin/sessionInfo/add",
  async (payload, { rejectWithValue }) => {
    try {
      debug("📡 ADD SESSION INFO PAYLOAD:", payload);

      const res = await createAdminSessionInfo(payload);

      debug("📥 ADD SESSION INFO RESPONSE:", res);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to create Session Instance."
      );
    }
  }
);

/* ============================================================
   UPDATE SESSION INFO (SYNCHRONIZE METADATA, LESSONS & ROSTER)
   The server applies only the differences in one transaction.
============================================================ */

export const updateSessionInfo = createAsyncThunk(
  "admin/sessionInfo/update",
  async (
    {
      id,
      sessionInfoData,
    },
    { rejectWithValue }
  ) => {
    try {
      debug("📡 UPDATE SESSION INFO PAYLOAD:", {
        id,
        sessionInfoData,
      });

      const res = await updateAdminSessionInfo(
        id,
        sessionInfoData
      );

      debug("📥 UPDATE SESSION INFO RESPONSE:", res);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update Session Instance and Roster."
      );
    }
  }
);

/* ============================================================
   DELETE SESSION INFO (CASCADE REMOVE)
============================================================ */

export const deleteSessionInfo = createAsyncThunk(
  "admin/sessionInfo/delete",
  async (
    id,
    { rejectWithValue }
  ) => {
    try {
      const res = await deleteAdminSessionInfo(id);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return {
        id,
        message: res.message,
      };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to delete Session Instance."
      );
    }
  }
);